import { useMemo } from 'react';
import * as React from 'react';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import { shuffle } from 'lodash';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { ANIMATIONS } from '../../utils';

const Wrapper = styled(motion.div)<{ $minimal: boolean }>`
  background: ${({ theme }) => theme.gradientFade};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 38px 0;
  position: relative;

  &::before {
    content: '';
    display: block;
    height: 1px;
    left: 0;
    top: 0;
    right: 0;
    background: linear-gradient(
      180deg,
      ${({ theme }) => rgba(theme.borderColor.MUTED, 0.1)} 0%,
      ${({ theme }) => rgba(theme.borderColor.MUTED, 0.1)} 100%
    );
    position: absolute;
    z-index: 2;
  }

  ${({ $minimal }) =>
    $minimal &&
    css`
      background: none;

      &::before {
        display: none;
      }
    `}
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
  width: 100%;
  max-width: 1165px;
  position: relative;
  padding: 0 30px;
  text-align: center;

  > span {
    font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    letter-spacing: 0;
  }
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 1000px;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    gap: 15px;
  }
`;

const Customer = styled.div`
  display: flex;
  height: 50px;
  flex: none;
`;

const Logo = styled.div`
  padding: 0 8px;
  height: 100%;

  img {
    ${({ theme }) => theme.customerLogoColor};
  }
`;

interface CustomerLogo {
  logo: {
    url: string;
  };
  name: string;
}

interface CustomerBannerQuery {
  allContentfulCustomer: {
    nodes: CustomerLogo[];
  };
}

interface CustomerBannerProps {
  logos?: CustomerLogo[];
  minimal?: boolean;
  title?: string;
}

export const CustomerBanner: React.ComponentType<CustomerBannerProps> =
  React.memo(({ minimal = false, title, logos = [] }) => {
    const data: CustomerBannerQuery = useStaticQuery(graphql`
      query {
        allContentfulCustomer(filter: { onHomePage: { eq: true } }) {
          nodes {
            name
            logo {
              url
            }
          }
        }
      }
    `);

    const theme = useTheme();
    const shouldCollapse = useMediaQuery(theme.mediaQueryString.SMALL);

    const logoArray = useMemo(
      () =>
        shouldCollapse || minimal
          ? shuffle(data.allContentfulCustomer.nodes).splice(0, 7)
          : shuffle(data.allContentfulCustomer.nodes),
      [data.allContentfulCustomer.nodes, shouldCollapse, minimal]
    );

    return (
      <Wrapper {...ANIMATIONS.SimpleFadeIn} $minimal={minimal}>
        <Title>
          <span>
            {title ? title : 'How the best data teams do their best work'}
          </span>
        </Title>
        <Logos>
          {(logos?.length ? logos : logoArray).map((item, index) => (
            <Customer key={index}>
              <Logo>
                <img alt={`Hex customer: ${item.name}`} src={item.logo.url} />
              </Logo>
            </Customer>
          ))}
        </Logos>
      </Wrapper>
    );
  });
